// ─── Query Key Factory ──────────────────────────────────────────────────────
//
// Zentrales Register aller Query Keys für TanStack Query.
// Jeder Key ist ein Array – das ermöglicht granulare Invalidierung.
//
// Beispiel:
//   invalidateQueries({ queryKey: QUERY_KEYS.persons.all })  → alle Personen-Listen
//   invalidateQueries({ queryKey: QUERY_KEYS.persons.detail('abc') })  → eine Person

export const QUERY_KEYS = {
  // ─── Personen ───────────────────────────────────────────────────────────────
  persons: {
    /** Basis-Key für alle Personen-Queries */
    all: ['persons'] as const,
    /** Personenliste mit Such-/Paginierungsparametern */
    list: (params?: Record<string, unknown>) => ['persons', 'list', params] as const,
    /** Einzelne Person nach ID */
    detail: (id: string) => ['persons', 'detail', id] as const,
  },

  // ─── Baumstruktur ─────────────────────────────────────────────────────────
  tree: {
    /** Gesamter Baum */
    all: ['tree'] as const,
    /** Teilbaum ab einer Person */
    subtree: (id: string, depth?: number) => ['tree', 'subtree', id, depth] as const,
  },

  // ─── Beziehungen ──────────────────────────────────────────────────────────
  relationships: {
    /** Basis-Key für alle Beziehungs-Queries */
    all: ['relationships'] as const,
    /** Beziehungen einer Person */
    byPerson: (personId: string) => ['relationships', 'byPerson', personId] as const,
  },

  // ─── Familie (Kind-Eltern) ────────────────────────────────────────────────
  familyMembers: {
    /** Basis-Key für alle FamilyMember-Queries */
    all: ['familyMembers'] as const,
  },

  // ─── Medizin & Vererbung ────────────────────────────────────────────────
  medical: {
    all: ['medical'] as const,
    conditions: (personId: string) => ['medical', 'conditions', personId] as const,
    traits: (personId: string) => ['medical', 'traits', personId] as const,
    consents: (personId: string) => ['medical', 'consents', personId] as const,
    catalog: (params?: Record<string, unknown>) => ['medical', 'catalog', params] as const,
  },

  // ─── Benutzer (Admin-Verwaltung) ──────────────────────────────────────────
  users: {
    /** Alle Benutzer */
    all: ['users'] as const,
  },

  // ─── Archiv ───────────────────────────────────────────────────────────────
  archive: {
    /** Basis-Key für alle Archiv-Queries */
    all: ['archive'] as const,
    /** Archiv-Liste mit Filtern */
    list: (params?: Record<string, unknown>) => ['archive', 'list', params] as const,
    /** Einzelnes Archiv-Element */
    detail: (id: string) => ['archive', 'detail', id] as const,
    /** Statistiken / Kategorie-Übersicht */
    stats: ['archive', 'stats'] as const,
  },

  // ─── Alben ────────────────────────────────────────────────────────────────
  albums: {
    all: ['albums'] as const,
  },

  // ─── Laufbahn & Lebensläufe ───────────────────────────────────────────────
  career: {
    /** Basis-Key für alle Lebenslauf-Queries (Liste + Texte) */
    biographies: ['biographies'] as const,
    /** Liste aller gepflegten Lebenslauf-Texte (Archiv) */
    biographiesList: ['biographies', 'list'] as const,
    /** Lebenslauf-Text einer Person */
    biography: (personId: string) => ['biographies', 'detail', personId] as const,
    /** Generierte Lebens-Chronik einer Person */
    chronicle: (personId: string) => ['chronicle', personId] as const,
  },

  // ─── Rollen (Admin) ───────────────────────────────────────────────────────
  roles: {
    all: ['roles'] as const,
    permissions: ['roles', 'permissions'] as const,
  },

  // ─── Herkunft ─────────────────────────────────────────────────────────────
  origins: {
    byPerson: (id: string) => ['origins', 'byPerson', id] as const,
  },
} as const;
